<template>
  <div>
    <span v-if="showSectionHeader"
          class="text-black font-semibold border-solid border-0 border-t border-gray-border pt-6 pl-8 block"
    >
      PRIMARY MARKET
    </span>

    <div :class="contentClass">
      <div v-if="hasExistingMarketOptions && !existingOnly" class="mt-4">
        <base-label id="market-mode" label="Market source" :label-classes="'font-medium'"/>
        <BaseToggleGroup class="mt-2"
                         :tabs="toggleTabs"
                         :value="value.mode"
                         @input="updateField('mode', $event)"
        />
      </div>

      <div v-if="showExistingMarketSelect" class="mt-4">
        <base-label id="existingMarketSelect" :label="marketSelectLabel" :label-classes="'font-medium'"/>
        <queryable-select id="market"
                          :value="value.sourceMarketId"
                          :options="marketOptions"
                          class="w-full mt-1 h-10"
                          @input="updateField('sourceMarketId', $event)"
        />
      </div>

      <div v-else-if="!existingOnly" class="mt-4">
        <base-label id="newMarketName" label="Name"/>
        <base-input id="newMarketName"
                    name="newMarketName"
                    variant="editor"
                    type="string"
                    class="w-full border-gray py-px mt-1 h-10"
                    :value="value.name"
                    @input="updateField('name', $event)"
        />

        <div class="grid grid-cols-2 gap-4 mt-4">
          <div class="col-span-1">
            <base-label id="newMarketLanguage" label="Language"/>
            <queryable-select id="newMarketLanguage"
                              label="Language"
                              :value="value.language"
                              :options="languageOptions"
                              class="w-full mt-1 h-10"
                              @input="updateField('language', $event)"
            />
          </div>

          <div class="col-span-1">
            <base-label id="newMarketTimezone" label="Timezone"/>
            <queryable-select id="newMarketTimezone"
                              label="Timezone"
                              :value="value.timezone"
                              :options="timezoneOptions"
                              class="w-full mt-1 h-10"
                              @input="updateField('timezone', $event)"
            />
          </div>
        </div>
      </div>

      <div v-if="showTimezoneFallback" class="mt-4">
        <base-label id="existingMarketTimezone" label="Timezone" :label-classes="'font-medium'"/>
        <queryable-select id="existingMarketTimezone"
                          label="Timezone"
                          :value="value.timezone"
                          :options="timezoneOptions"
                          class="w-full mt-1 h-10"
                          @input="updateField('timezone', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BaseToggleGroup from '@/components/base-components/BaseToggleGroup.vue'
import QueryableSelect from '@/components/common/QueryableSelect.vue'
import { BOT_LANGUAGES, BOT_TIMEZONES } from '@/utils/bot.constants'
import {
  MARKET_MODE,
  getAvailableMarketOptions,
  marketHasNoTimezone
} from '@/utils/market.utils'
import { getAvailableMarketOptionsForIndexGroup } from '@/utils/index.utils'
import marketModule from '@/store/modules/market.module'
import botModule from '@/store/modules/bot.module'

export default {
  name: 'SettingsBotMarketForm',

  components: {
    BaseToggleGroup,
    QueryableSelect
  },

  props: {
    value: {
      type: Object,
      required: true
    },
    multiBots: {
      type: Array,
      default: () => []
    },
    indexes: {
      type: Array,
      default: () => []
    },
    excludeMultiBotId: {
      type: String,
      default: null
    },
    excludeIndexGroupId: {
      type: String,
      default: null
    },
    showSectionHeader: {
      type: Boolean,
      default: false
    },
    // When true, only allow selecting an existing market (no create-new toggle/form).
    existingOnly: {
      type: Boolean,
      default: false
    },
    marketSelectLabel: {
      type: String,
      default: 'Select Market'
    },
    // When true, allow showing the Timezone fallback field for a bot-less selected market.
    allowTimezoneFallback: {
      type: Boolean,
      default: false
    },
    contentClass: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      MARKET_MODE
    }
  },

  computed: {
    markets() {
      return marketModule.getMarketList()
    },
    bots() {
      return botModule.getBotList()
    },
    toggleTabs() {
      return [
        { key: MARKET_MODE.EXISTING, label: 'Use Existing Market' },
        { key: MARKET_MODE.NEW, label: 'Create New Market' }
      ]
    },
    marketOptions() {
      if (this.excludeIndexGroupId) {
        return getAvailableMarketOptionsForIndexGroup(this.indexes, this.excludeIndexGroupId)
      }
      return getAvailableMarketOptions(this.multiBots, this.excludeMultiBotId)
    },
    hasExistingMarketOptions() {
      return this.marketOptions.length > 0
    },
    showExistingMarketSelect() {
      if (!this.hasExistingMarketOptions) return false
      return this.existingOnly || this.value.mode === MARKET_MODE.EXISTING
    },
    showTimezoneFallback() {
      return this.allowTimezoneFallback && this.existingOnly && !!this.value.sourceMarketId &&
        marketHasNoTimezone(this.value.sourceMarketId, this.bots)
    },
    languageOptions() {
      return BOT_LANGUAGES
    },
    timezoneOptions() {
      return BOT_TIMEZONES.map(timezone => ({
        text: timezone,
        value: timezone
      }))
    }
  },

  watch: {
    hasExistingMarketOptions: {
      immediate: true,
      handler(hasOptions) {
        if (this.existingOnly) {
          if (this.value.mode !== MARKET_MODE.EXISTING) {
            this.emitValue({
              ...this.value,
              mode: MARKET_MODE.EXISTING
            })
          }
          return
        }
        if (!hasOptions && this.value.mode !== MARKET_MODE.NEW) {
          this.emitValue({
            ...this.value,
            mode: MARKET_MODE.NEW,
            sourceMarketId: null
          })
        }
      }
    }
  },

  methods: {
    updateField(field, fieldValue) {
      const nextValue = {
        ...this.value,
        [field]: fieldValue
      }
      if (field === 'mode' && fieldValue === MARKET_MODE.NEW) {
        nextValue.sourceMarketId = null
      }
      this.emitValue(nextValue)
    },
    emitValue(nextValue) {
      this.$emit('input', nextValue)
    }
  }
}
</script>
